import { FormItemsGroup, SkeletonBox, Spacing, Text } from '@billy/design-system'
import type { GridTemplateValue } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'

import { isInvoiceFormDepositLine } from '@features/invoices/utils/depositInvoices/isInvoiceFormDepositLine'

import { useInvoiceEditorDepositModal } from '../../../../../../../../contexts/invoiceEditorDepositModalContext'
import { useInvoiceEditorForm } from '../../../../../../../../contexts/invoiceEditorFormContext'
import { useInvoiceLineHeaderLabels } from '../../../../hooks/useInvoiceLineHeaderLabels'
import { DepositInvoiceMultiVatLines } from './elements/DepositInvoiceMultiVatLines'
import { DepositInvoiceSingleVatLine } from './elements/DepositInvoiceSingleVatLine'
import * as Styled from './styles'

const depositSingleVatColumnItemsTemplate: GridTemplateValue[] = [80, 17, 11, 17, 4]
const depositMultiVatColumnItemsTemplate: GridTemplateValue[] = [83, 17, 11, 17, 1]

export const DepositInvoiceLines = (): ReactElement => {
  const { invoiceEditorForm, isLoading, trackInvoiceEditor } = useInvoiceEditorForm()
  const { getHeaderLabel } = useInvoiceLineHeaderLabels()
  const { openDepositInvoiceDetailsModal } = useInvoiceEditorDepositModal()
  const { watch } = invoiceEditorForm
  const invoiceLines = watch('invoiceLines')

  const handleEditButtonClick = useCallback(() => {
    trackInvoiceEditor('Form - Invoice lines - Edit deposit amount clicked')
    openDepositInvoiceDetailsModal()
  }, [openDepositInvoiceDetailsModal, trackInvoiceEditor])

  const depositLines = invoiceLines.filter(isInvoiceFormDepositLine)
  const isMultiVat = depositLines.length > 1
  const columnItemsTemplate = isMultiVat ? depositMultiVatColumnItemsTemplate : depositSingleVatColumnItemsTemplate

  let depositLinesContent: ReactElement = <></>

  if (isLoading) {
    depositLinesContent = <SkeletonBox fullWidth height={200} />
  } else if (isMultiVat) {
    depositLinesContent = (
      <DepositInvoiceMultiVatLines
        columnItemsTemplate={columnItemsTemplate}
        depositLines={depositLines}
        onEditClick={handleEditButtonClick}
      />
    )
  } else if (depositLines[0]) {
    depositLinesContent = (
      <DepositInvoiceSingleVatLine
        columnItemsTemplate={columnItemsTemplate}
        depositLine={depositLines[0]}
        onEditClick={handleEditButtonClick}
      />
    )
  }

  return (
    <>
      <FormItemsGroup itemsInRow={columnItemsTemplate.length} itemsTemplate={columnItemsTemplate} spacing={Spacing.S4}>
        <Text variant="bodySM" weight="bold">
          {getHeaderLabel('description')}
        </Text>
        <Text alignment="right" variant="bodySM" weight="bold">
          {getHeaderLabel('netTotal')}
        </Text>
        <Text alignment="right" variant="bodySM" weight="bold">
          {getHeaderLabel('vatPct')}
        </Text>
        <Text alignment="right" variant="bodySM" weight="bold">
          {getHeaderLabel('total')}
        </Text>
        <Styled.EmptyGridCell />
      </FormItemsGroup>
      <Styled.DepositInvoiceLineWrapper>{depositLinesContent}</Styled.DepositInvoiceLineWrapper>
    </>
  )
}
